import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import DashboardAccessBlocked from "./DashboardAccessBlocked";
import {
  fetchDashboardAccess,
  getUsernameForAccessCheck,
  isDashboardAccessBlocked,
} from "../utils/dashboardAccess";
import {
  PASSWORD_EXPIRY_EVENT,
  isPasswordExpired,
  setPasswordExpired,
} from "../utils/passwordExpiry";
import { getPasswordStatus } from "../controllers/userController";
import { isSuperAdmin } from "../controllers/superAdminController";

const POLL_INTERVAL_MS = 30000;

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const [accessState, setAccessState] = useState({
    loading: true,
    blocked: false,
    dueDate: null,
  });
  // Seeded from the flag /signin set, so the gate is up on the first paint
  // instead of after the first poll returns.
  const [passwordExpired, setPasswordExpiredState] = useState(isPasswordExpired);

  const refreshAccess = useCallback(async () => {
    // A super admin needs to reach an account whose dashboard is switched off,
    // which is the whole point of looking at it. The backend exempts their
    // session too (see dashboard_access_middleware.py).
    if (isSuperAdmin()) {
      setAccessState({ loading: false, blocked: false, dueDate: null });
      return;
    }

    const username = getUsernameForAccessCheck();
    if (!username) {
      setAccessState({ loading: false, blocked: false, dueDate: null });
      return;
    }

    try {
      const access = await fetchDashboardAccess(username);
      setAccessState({
        loading: false,
        blocked: isDashboardAccessBlocked(access),
        dueDate: access?.billing_due_date || null,
      });
    } catch (error) {
      console.error("Dashboard access check failed:", error);
      setAccessState((current) => ({ ...current, loading: false }));
    }
  }, []);

  // Passwords expire every 2 months. /signin reports the state at login and
  // utils/api.js catches a 403 PASSWORD_EXPIRED mid-session; this poll is the
  // third path, covering a session left open across the expiry date.
  const refreshPasswordStatus = useCallback(async () => {
    if (isSuperAdmin()) {
      setPasswordExpiredState(false);
      return;
    }

    const username = getUsernameForAccessCheck();
    if (!username) return;

    try {
      const status = await getPasswordStatus(username);
      setPasswordExpired(
        status?.password_expired === true,
        status?.password_expires_at || null,
      );
      setPasswordExpiredState(status?.password_expired === true);
    } catch (error) {
      // Leave the current verdict alone: a failed check must not let a stale
      // password through, nor lock out an account over a transient error.
      console.error("Password status check failed:", error);
    }
  }, []);

  useEffect(() => {
    if (!token || token === "undefined") {
      return undefined;
    }

    refreshAccess();
    refreshPasswordStatus();
    const intervalId = window.setInterval(() => {
      refreshAccess();
      refreshPasswordStatus();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [token, refreshAccess, refreshPasswordStatus]);

  // Raised by the api interceptor when any request comes back 403
  // PASSWORD_EXPIRED, and lowered by PasswordExpired once a new one is set.
  useEffect(() => {
    const onExpiryChange = (event) =>
      setPasswordExpiredState(event?.detail?.expired === true);
    window.addEventListener(PASSWORD_EXPIRY_EVENT, onExpiryChange);
    return () => window.removeEventListener(PASSWORD_EXPIRY_EVENT, onExpiryChange);
  }, []);

  if (!token || token === "undefined") {
    return <Navigate to="/login" replace />;
  }

  // Checked before the billing state resolves: with an expired password every
  // other endpoint answers 403, including the access check itself, so there is
  // nothing to wait for. The login card handles the change in its own "update"
  // mode rather than a separate screen, so send the user there.
  if (passwordExpired) {
    return <Navigate to="/login" replace />;
  }

  if (accessState.loading) {
    return null;
  }

  if (accessState.blocked) {
    return (
      <DashboardAccessBlocked
        dueDate={accessState.dueDate}
        onRetry={refreshAccess}
      />
    );
  }

  return children;
};

export default PrivateRoute;
